import { useEffect, useState } from "react";
import { Swords, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { successPop } from "@/lib/celebrate";

const STAKES = [10, 20, 50, 100];

export function DuelButton({ userId, name, className }) {
  const { user, refreshUser } = useAuth();
  const [friends, setFriends] = useState(false);
  const [open, setOpen] = useState(false);
  const [stake, setStake] = useState(20);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/friends/status/${userId}`).then(({ data }) => setFriends(data.status === "friends")).catch(() => {}); }, [userId]);
  if (!friends) return null;
  const send = async (e) => {
    const el = e.currentTarget;
    setBusy(true);
    try { await api.post("/duels", { opponent_id: userId, stake }); successPop(el); toast.success(`${name.split(" ")[0]} wurde herausgefordert ⚔️`); refreshUser(); setOpen(false); }
    catch (er) { toast.error(er?.response?.data?.detail || "Das hat nicht geklappt"); } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="profile-duel-btn" className={`${className} bg-gradient-to-r from-violet-500 to-pink-500 text-white`}>
        <Swords className="w-4 h-4" /> <span>Zum Duell herausfordern</span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-sm" data-testid="profile-duel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Swords className="w-5 h-5 text-fuchsia-500" /> Duell gegen {name.split(" ")[0]}</DialogTitle>
            <DialogDescription>Ihr setzt beide gleich viele Punkte. Wer in 24 Stunden mehr Punkte sammelt, gewinnt alles.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-4 gap-2">
            {STAKES.map((v) => <button key={v} type="button" onClick={() => setStake(v)} data-testid={`profile-duel-stake-${v}`}
              className={`h-12 rounded-xl font-extrabold transition-colors ${stake === v ? "pink-gradient text-white" : "bg-rose-50 text-rose-600"}`}>{v}</button>)}
          </div>
          <p className="text-xs text-slate-500 text-center">Gewinn: <b className="text-rose-600">{stake * 2} Punkte</b> · Dein Stand: {user?.points ?? 0}</p>
          <button type="button" disabled={busy || (user?.points ?? 0) < stake} onClick={send} data-testid="profile-duel-send"
            className="h-12 rounded-full pink-gradient text-white font-extrabold inline-flex items-center justify-center gap-2 disabled:opacity-50 active:scale-95 transition-transform">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Swords className="w-5 h-5" />} Herausfordern · {stake} Punkte
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function DuelBadges({ badges = [], testId = "duel-badges" }) {
  if (!badges.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {[...badges].reverse().map((b) => (
        <span key={b.month} title={`Duell-König ${b.label} (${b.wins} Siege)`} data-testid={`${testId}-${b.month}`}
          className="climber-badge inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-extrabold text-white bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500 shadow-md shadow-orange-300/50">
          ⚔️👑 Duell-König {b.label}
        </span>
      ))}
    </div>
  );
}
